/**
 * §2.6 — stage a VIN for a buyer chosen by hand.
 *
 * The cohort engine cannot know about a relationship the broker does (a standing
 * order, a buyer moving into a new segment), so this is the manual override. It
 * is the same search `ManualBuyerAssign` uses in the VIN pop-out, lifted into its
 * own component because the basket needs it in two places — on a VIN row in the
 * VIN slice, and on a staged VIN in the buyer slice.
 *
 * Copy, never move (§2.0 decision #1): a VIN stays with every buyer it was
 * pitched to, so `excludeUids` disables the ones already holding it rather than
 * hiding them — "already staged" is useful information, not noise.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BUYER_5MIL_HELP } from './brokerViewTokens';
import { siftApiClient } from '../../api/siftApiClient';
import type { BrokerBuyerSearchResult } from '../../api/types';

const fmtMiles = (n?: number | null) =>
  n == null ? '—' : `${Math.round(n).toLocaleString()} mi`;

export const BuyerPicker: React.FC<{
  label: string;
  title?: string;
  className?: string;
  /** Buyers already staged against this VIN — shown, but not addable again. */
  excludeUids: Set<string>;
  /** The VIN being staged. Scopes the search so each result carries its distance
   *  from that VIN's anchor, which is most of what makes a manual pick judgeable. */
  vin?: string;
  /** When given, pick from these instead of searching every buyer. The basket
   *  slice uses it to keep "copy to another buyer I am already working" a short
   *  list rather than a search problem. */
  candidates?: { buyer_uid: string; buyer_name: string | null }[];
  onPick: (buyer: { buyer_uid: string; buyer_name: string | null }) => void;
}> = ({ label, title, className, excludeUids, vin, candidates, onPick }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BrokerBuyerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const scoped = !!candidates;

  const shown = useMemo(() => {
    if (!candidates) return results;
    const q = query.trim().toLowerCase();
    const matches = q
      ? candidates.filter((c) => (c.buyer_name ?? '').toLowerCase().includes(q) || c.buyer_uid.includes(q))
      : candidates;
    return [...matches].sort((a, b) =>
      (a.buyer_name || a.buyer_uid).localeCompare(b.buyer_name || b.buyer_uid));
  }, [candidates, results, query]);

  useEffect(() => {
    if (!open || scoped) return;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await siftApiClient.searchBrokerBuyers(query.trim() || undefined, 15, controller.signal, vin));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [open, query, scoped, vin]);

  if (!open) {
    return (
      <button
        type="button"
        className={className ?? 'bb-addbuyer-toggle'}
        onClick={() => setOpen(true)}
        title={title ?? 'Stage this VIN for another buyer'}
      >{label}</button>
    );
  }

  return (
    <div className="bb-addbuyer">
      <div className="bb-addbuyer-head">
        <input
          autoFocus
          className="bb-addbuyer-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={scoped ? 'Filter basket buyers…' : 'Search by 5mil or name…'}
          aria-label={scoped
            ? 'Filter basket buyers to stage this VIN for'
            : 'Search buyers to stage this VIN for'}
        />
        <button
          type="button"
          className="bb-addbuyer-cancel"
          onClick={() => { setOpen(false); setQuery(''); }}
        >Cancel</button>
      </div>
      {!scoped && searching && <div className="bb-suggest-note">Searching…</div>}
      {!searching && shown.length === 0 && (
        <div className="bb-suggest-note">
          {scoped
            ? (query.trim()
              ? 'No basket buyers match that.'
              : 'No other buyers in the basket yet.')
            : 'No buyers match that search.'}
        </div>
      )}
      {scoped && shown.length > 0 && shown.every((r) => excludeUids.has(r.buyer_uid)) && (
        <div className="bb-suggest-note">Every basket buyer already has this VIN.</div>
      )}
      {shown.map((r) => {
        const already = excludeUids.has(r.buyer_uid);
        // The basket-scoped list is picked from buyers already staged, so it
        // carries no search metadata to show.
        const hit = 'distance_miles' in r ? (r as BrokerBuyerSearchResult) : null;
        return (
          <div key={r.buyer_uid} className="bb-addbuyer-row">
            <span className="bb-addbuyer-name">
              {r.buyer_name || `Buyer ${r.buyer_uid}`}
              <span className="bb-addbuyer-uid" title={BUYER_5MIL_HELP}> · #{r.buyer_uid}</span>
              {hit?.state && <span className="bb-addbuyer-uid"> · {hit.state}</span>}
              {hit?.distance_miles != null && (
                <span
                  className="bb-addbuyer-uid"
                  title="Distance from this vehicle's proximity anchor"
                > · {fmtMiles(hit.distance_miles)}</span>
              )}
            </span>
            <button
              type="button"
              className={`bb-addbuyer-add${already ? ' is-added' : ''}`}
              disabled={already}
              onClick={() => {
                onPick({ buyer_uid: r.buyer_uid, buyer_name: r.buyer_name ?? null });
                setOpen(false);
                setQuery('');
              }}
              title={already ? 'Already staged for this VIN' : 'Stage this VIN for this buyer'}
            >{already ? '✓ Added' : '+ Add'}</button>
          </div>
        );
      })}
    </div>
  );
};
